<template>
  <div
    ref="contentModal"
    aria-hidden="true"
    :aria-labelledby="`modal-title-${modalId}`"
    :aria-describedby="ariaDescribedby"
    :class="['modal', 'fade', { '-stacked': stacked }]"
    :role="role"
    tabindex="-1"
  >
    <div :class="['modal-dialog', 'modal-dialog-centered', 'modal-' + size]" role="document">
      <div class="modal-content position-relative">
        <div class="modal-header">
          <h5 :id="`modal-title-${modalId}`" class="modal-title textStyle">
            {{ title }}
          </h5>
          <button
            :aria-label="$t('global.close')"
            class="btn-close"
            data-bs-dismiss="modal"
            data-dismiss="modal"
            type="button"
          ></button>
        </div>
        <div class="modal-body">
          <slot v-if="$slots.content" name="content"></slot>
          <!-- ST143-TI-17771 (c-group 3, KEEP + SANITIZE): shared cross-cutting component (7 call sites) — see
               §2.6 for the two untrusted callers (ModerationListView.vue re-rendering reported chat content plus
               a user-controlled filename, NotepadView.vue concatenating note.user.fullName). Sanitized here,
               once, rather than at each call site. -->
          <div v-else v-safe-html="{ value: content, profile: contentProfile }"></div>
          <div class="d-flex align-items-center justify-content-center mt-4" v-if="cancelBtn">
            <button class="btn btn-danger" data-bs-dismiss="modal" data-dismiss="modal" type="button">
              {{ $t("close") }}
            </button>
          </div>
        </div>
        <BlockLoader :loading="loading" />
      </div>
    </div>
  </div>
</template>

<script>
import BlockLoader from "@/components/blocks/BlockLoader.vue";
import { modalMixin } from "@/mixins/modalMixin";

export default {
  name: "ModalComponent",
  components: { BlockLoader },
  mixins: [modalMixin],
  props: {
    title: {
      type: String,
      required: true,
      default: "",
    },
    content: {
      type: String,
      required: false,
      default: "",
    },
    // ST143-TI-17771: lets callers rendering a mail body (e.g. UsersListView, SendUserInfosView) opt into the
    // more permissive "mail" sanitizer profile instead of the default WYSIWYG "richText" one.
    contentProfile: {
      type: String,
      required: false,
      default: "richText",
      validator: (v) => ["richText", "mail"].includes(v),
    },
    opened: {
      type: Boolean,
      required: true,
      default: false,
    },
    cancelBtn: {
      type: Boolean,
      required: false,
      default: false,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    size: {
      type: String,
      required: false,
      default: "xl",
      validator: (v) => ["sm", "md", "lg", "xl", "fullscreen"].includes(v),
    },
    // Prop explicite plutôt que fallthrough d'attributs : Bootstrap réécrit `role="dialog"` sur
    // l'élément à chaque ouverture (`Modal._showElement()`), d'où la réapplication dans onModalShown.
    role: {
      type: String,
      required: false,
      default: "dialog",
      validator: (v) => ["dialog", "alertdialog"].includes(v),
    },
    ariaDescribedby: {
      type: String,
      required: false,
      default: null,
    },
    // À activer quand cette modale s'ouvre par-dessus une autre modale Bootstrap déjà affichée :
    // Bootstrap 5 ne coordonne pas les modales empilées. Il ajoute un second `.modal-backdrop` (tous
    // à z-index 1060 côté projet), ce qui double l'assombrissement, et `Modal._hideModal()` retire
    // `modal-open` du `<body>` sans vérifier qu'une modale reste affichée — or cette classe sert de
    // marqueur d'état aux nettoyages de `modalMixin.beforeUnmount` et de `main.js`.
    stacked: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ["hidden"],
  data() {
    return {
      // Unique par instance : plusieurs ModalComponent peuvent coexister dans une même page (même
      // schéma que ModalFormTabsComponent).
      modalId: Math.random().toString(36).slice(2, 11),
    };
  },
  watch: {
    opened() {
      if (this.opened) {
        this.showModal(this.stacked ? { backdrop: false } : {});
      } else {
        this.hideModal();
      }
    },
  },
  mounted() {
    const el = this.getModalElement();
    if (el) {
      el.addEventListener("shown.bs.modal", this.onModalShown);
    }
  },
  beforeUnmount() {
    const el = this.getModalElement();
    if (el) {
      el.removeEventListener("shown.bs.modal", this.onModalShown);
    }
  },
  methods: {
    // `Modal._showElement()` écrase `role` avec "dialog" à chaque ouverture : on repose la valeur
    // demandée une fois la modale affichée.
    onModalShown() {
      this.getModalElement()?.setAttribute("role", this.role);
    },

    onModalHidden() {
      // Repose le marqueur `modal-open` que Bootstrap vient de retirer si une modale reste
      // affichée (cf. prop `stacked`), avant que le mixin émette `hidden`.
      if (this.stacked && document.querySelector(".modal.show")) {
        document.body.classList.add("modal-open");
      }
      modalMixin.methods.onModalHidden.call(this);
    },
  },
};
</script>

<style scoped lang="scss">
// `.modal` est fixé globalement à z-index 1099 (assets/sass/style.scss) : une modale empilée doit
// passer au-dessus de celle qui reste ouverte, sans dépendre de l'ordre du DOM.
.modal.-stacked {
  z-index: 1100;
}
</style>
